import React, { useState } from 'react';
import { ErrorBox } from './common/ErrorBox';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { useFormContext } from 'react-hook-form';
import { InfoModal } from '../components/InfoModal';

const RadioPicker = ({
  name,
  label,
  title,
  errors,
  detailed_label,
  validation,
  options = [],
  defaultValue,
  description,
  disabled = false
}) => {
  const { register, setValue, getValues, watch } = useFormContext();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Get current value from form or default
  const currentValue = watch(name) || defaultValue;

  // Set initial value on mount
  React.useEffect(() => {
    const formValue = getValues(name);
    if ((formValue === undefined || formValue === '') && defaultValue) {
      setValue(name, defaultValue, { shouldValidate: false });
    }
  }, [name, defaultValue, setValue, getValues]);

  // Handle option selection
  const handleSelect = (option) => {
    if (disabled) return;
    setValue(name, option.value, { shouldValidate: true });
  };

  return (
    <>
      <div className="bg-white/60 rounded-2xl shadow-sm border border-gray-200/60 p-3.5 transition-all duration-200 hover:bg-white/80 hover:shadow-md backdrop-blur-sm">
        <div className="flex justify-between items-start mb-2">
          <h2 className='text-base font-semibold text-gray-900'>{title}</h2>
          <AiOutlineInfoCircle
            className="text-gray-600 text-base cursor-pointer hover:text-gray-800 transition-colors flex-shrink-0"
            onClick={() => setIsModalOpen(true)}
          />
        </div>

        <p className="text-sm text-gray-600 mb-3">{detailed_label}</p>

        {/* Radio Options */}
        <div className="flex flex-col gap-2">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option)}
              disabled={disabled}
              className={`w-full p-3 text-left rounded-xl border-2 transition-all duration-200 ${
                currentValue === option.value
                  ? 'border-gray-800 bg-gray-50'
                  : 'border-gray-200 bg-white/80 hover:border-gray-300 hover:bg-white'
              } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
            >
              <div className="flex items-center gap-3">
                {/* Radio circle */}
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                  currentValue === option.value
                    ? 'border-gray-800'
                    : 'border-gray-300'
                }`}>
                  {currentValue === option.value && (
                    <div className="w-2.5 h-2.5 rounded-full bg-gray-800" />
                  )}
                </div>

                {/* Label and description */}
                <div className="flex flex-col flex-1">
                  <span className={`font-medium text-sm ${
                    currentValue === option.value ? 'text-gray-900' : 'text-gray-700'
                  }`}>
                    {option.label}
                  </span>
                  {option.description && (
                    <span className="text-xs text-gray-500 mt-0.5">{option.description}</span>
                  )}
                </div>

                {/* Badge */}
                {option.badge && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ml-auto ${
                    currentValue === option.value
                      ? 'bg-gray-800 text-white'
                      : 'bg-gray-200 text-gray-700'
                  }`}>
                    {option.badge}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Hidden input for form registration */}
        <input
          type="hidden"
          {...register(name, validation)}
          value={currentValue || ''}
        />

        <ErrorBox errors={errors} />
      </div>

      <InfoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={title}
      >
        <p className="text-sm leading-relaxed">{description}</p>
      </InfoModal>
    </>
  );
};

export default RadioPicker;
